import { z } from 'zod'

import type {
  ManualOperationEntry,
  ManualOperationOwner,
  ManualOperationState,
} from './manual-operation'
import {
  confirmContractorInvoiceFinancialResponse,
  confirmExpenseFinancialResponse,
  confirmFinancialCommandClosure,
} from '../types/financial-response'

/** Reservation and matched deletion must be atomic across host storage connections. */
export interface FinancialIntentStore {
  read(key: string): Promise<string | null>
  reserve(
    key: string,
    candidate: string,
  ): Promise<{ value: string; created: boolean }>
  removeIfValue(key: string, expected: string): Promise<boolean>
}
const uuid = z.string().uuid()
const ownerSchema = z.object({
  ownerId: uuid,
  organizationId: uuid,
  relationshipId: uuid.nullable(),
})
export const FinancialOperationPayloadSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('expense.reimburse'),
      amount: z
        .number()
        .finite()
        .positive()
        .refine(value => Number(value.toFixed(2)) === value),
      reimbursedAt: z.string().datetime({ offset: true }),
      reference: z.string().max(255).optional(),
      note: z.string().optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('contractor-invoice.mark-paid'),
      paidAt: z.string().datetime().optional(),
    })
    .strict(),
  z.object({ action: z.literal('contractor-invoice.unmark-paid') }).strict(),
])
export type FinancialOperationPayload = z.infer<
  typeof FinancialOperationPayloadSchema
>
export interface FinancialOperationEntry extends ManualOperationEntry<FinancialOperationPayload> {
  recordId: string
  recordKind: 'expense' | 'contractor-invoice'
}
export interface FinancialOperationState extends Omit<
  ManualOperationState<FinancialOperationPayload>,
  'pending'
> {
  pending?: FinancialOperationEntry
}
export class FinancialOperationRecoveryError extends Error {
  constructor() {
    super(
      'This saved financial request cannot be read safely. Keep it for review; no new change has been sent.',
    )
    this.name = 'FinancialOperationRecoveryError'
  }
}
const pendingMessage =
  'This request is unresolved. Retry the saved request, or ask Scrambl to confirm or cancel it before starting another change.'

/** No background replay, expiry, blanket 4xx clearing or local history-based discard.
 * Every dispatched failure retains the exact intent until an applied receipt or
 * terminal server closure is confirmed. Network work is outside storage transactions.
 */
export function createFinancialOperation(options: {
  store: FinancialIntentStore
  owner: ManualOperationOwner
  recordId: string
  recordKind: FinancialOperationEntry['recordKind']
  newId(): string
  assertCurrent(): void
}) {
  const owner = {
    ownerId: options.owner.ownerId.toLowerCase(),
    organizationId: options.owner.organizationId.toLowerCase(),
    relationshipId: options.owner.relationshipId?.toLowerCase() ?? null,
  }
  const recordId = options.recordId.toLowerCase()
  const recordKind = options.recordKind
  const key = `financial-operation:v1:${JSON.stringify([owner.ownerId, owner.organizationId, owner.relationshipId, recordKind, recordId])}`
  let state: FinancialOperationState = {
    phase: 'loading',
    message: 'Checking for saved financial requests…',
  }
  let busy = false
  const listeners = new Set<() => void>()
  function update(value: typeof state) {
    state = Object.freeze(value)
    listeners.forEach(listener => listener())
  }
  function assertCurrent() {
    options.assertCurrent()
    if (
      !ownerSchema.safeParse(owner).success ||
      !uuid.safeParse(recordId).success
    )
      throw new FinancialOperationRecoveryError()
  }
  function payload(value: unknown): FinancialOperationPayload {
    const parsed = FinancialOperationPayloadSchema.safeParse(value)
    if (!parsed.success || !parsed.data.action.startsWith(`${recordKind}.`))
      throw new FinancialOperationRecoveryError()
    return parsed.data
  }
  function decode(raw: string): FinancialOperationEntry {
    try {
      const parsed = z
        .object({
          ...ownerSchema.shape,
          version: z.literal(1),
          operationId: uuid,
          label: z.string().min(1).max(180),
          recordId: uuid,
          recordKind: z.enum(['expense', 'contractor-invoice']),
          payload: FinancialOperationPayloadSchema,
        })
        .strict()
        .parse(JSON.parse(raw))
      if (
        parsed.ownerId !== owner.ownerId ||
        parsed.organizationId !== owner.organizationId ||
        parsed.relationshipId !== owner.relationshipId ||
        parsed.recordId !== recordId ||
        parsed.recordKind !== recordKind
      )
        throw new FinancialOperationRecoveryError()
      return Object.freeze({
        ...owner,
        version: 1 as const,
        operationId: parsed.operationId,
        label: parsed.label,
        recordId,
        recordKind,
        payload: Object.freeze(payload(parsed.payload)),
      })
    } catch {
      throw new FinancialOperationRecoveryError()
    }
  }
  async function read() {
    assertCurrent()
    const raw = await options.store.read(key)
    assertCurrent()
    return raw === null ? undefined : { raw, entry: decode(raw) }
  }
  async function showCurrent() {
    const saved = await read()
    update(
      saved
        ? { phase: 'pending', pending: saved.entry, message: pendingMessage }
        : { phase: 'ready' },
    )
  }
  async function run<R>(
    kind: 'new' | 'retry' | 'close',
    request: (entry: FinancialOperationEntry) => Promise<R>,
    input?: unknown,
    label?: string,
  ): Promise<{ result: R } | undefined> {
    if (busy || (kind === 'new' && state.phase !== 'ready')) return undefined
    busy = true
    let saved: { raw: string; entry: FinancialOperationEntry } | undefined
    let reservationAttempted = false
    update({ ...state, phase: 'sending', message: undefined })
    try {
      assertCurrent()
      if (kind === 'new') {
        const entry: FinancialOperationEntry = {
          ...owner,
          version: 1,
          recordId,
          recordKind: recordKind,
          operationId: uuid.parse(options.newId()).toLowerCase(),
          label: label ?? 'Financial request',
          payload: payload(JSON.parse(JSON.stringify(input))),
        }
        const raw = JSON.stringify(entry)
        decode(raw) // Validate the complete envelope before reserving durable bytes.
        reservationAttempted = true
        const claim = await options.store.reserve(key, raw)
        assertCurrent()
        saved = { raw: claim.value, entry: decode(claim.value) }
        if (!claim.created || claim.value !== raw) {
          update({
            phase: 'pending',
            pending: saved.entry,
            message: pendingMessage,
          })
          return undefined
        }
      } else saved = await read()
      if (!saved) {
        update({ phase: 'ready' })
        return undefined
      }
      update({ phase: 'sending', pending: saved.entry })
      assertCurrent()
      const result = await request(saved.entry)
      assertCurrent()
      if (kind === 'close') {
        confirmFinancialCommandClosure(result, {
          operationId: saved.entry.operationId,
          organizationId: owner.organizationId,
          userId: owner.ownerId,
          targetId: recordId,
          action: saved.entry.payload.action,
        })
      } else {
        const expected = {
          recordId,
          organizationId: owner.organizationId,
          operationId: saved.entry.operationId,
        }
        if (recordKind === 'expense')
          confirmExpenseFinancialResponse(result, expected)
        else confirmContractorInvoiceFinancialResponse(result, expected)
      }
      assertCurrent()
      const removed = await options.store.removeIfValue(key, saved.raw)
      assertCurrent()
      if (!removed) {
        await showCurrent()
        return undefined
      }
      update({ phase: 'ready' })
      return { result }
    } catch (error) {
      // The store itself may have committed a reservation before its response was lost.
      // Never replace or discard it on an uncertain acknowledgement.
      if (kind === 'new' && !reservationAttempted && !saved) {
        try {
          await showCurrent()
        } catch {
          update({
            phase: 'blocked',
            message: 'Saved financial requests are unavailable.',
          })
        }
        throw error
      }
      try {
        assertCurrent()
        saved ??= await read()
        update(
          saved
            ? {
                phase: 'pending',
                pending: saved.entry,
                message: pendingMessage,
              }
            : {
                phase: 'blocked',
                message:
                  'Saved financial requests are unavailable. No replacement request will be sent. Reopen this record to recover.',
              },
        )
      } catch {
        update({
          phase: 'blocked',
          message:
            'This saved financial request needs recovery. Reopen this record in its original account and workspace.',
        })
      }
      throw error
    } finally {
      busy = false
    }
  }
  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    async load() {
      if (busy) return
      busy = true
      try {
        await showCurrent()
      } catch {
        update({
          phase: 'blocked',
          message:
            'Saved financial requests are unavailable. Reopen this record in its original account and workspace.',
        })
      } finally {
        busy = false
      }
    },
    submit: <R>(
      input: FinancialOperationPayload,
      label: string,
      request: (entry: FinancialOperationEntry) => Promise<R>,
    ) => run('new', request, input, label),
    retry: <R>(request: (entry: FinancialOperationEntry) => Promise<R>) =>
      run('retry', request),
    close: <R>(request: (entry: FinancialOperationEntry) => Promise<R>) =>
      run('close', request),
  }
}
